import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import "./TestimonialsSection.css";

const getPageTestimonialType = (pathname, menusData) => {
  if (!menusData || menusData.length === 0) return null;
  const flattenMenus = (menus) => {
    let result = [];
    menus.forEach((menu) => {
      result.push(menu);
      if (menu.children?.length > 0)
        result = result.concat(flattenMenus(menu.children));
    });
    return result;
  };
  const allMenus = flattenMenus(menusData);
  const matchedMenu = allMenus.find((menu) => menu.url === pathname);
  return matchedMenu?.slug || null;
};

const TestimonialsSection = ({
  title = "What Our Clients Say",
  subtitle = "Real experiences from our satisfied customers",
}) => {
  const [testimonials, setTestimonials] = useState([]);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const marqueeRef = useRef(null);
  const animationRef = useRef(null);
  const isSwipingRef = useRef(false);
  const isHoveringRef = useRef(false);
  const touchTimeoutRef = useRef(null);

  const location = useLocation();
  const isHomePage = location.pathname === "/" || location.pathname === "/home";
  const { menus = [], loading: menuLoading } = useSelector(
    (state) => state.menu || {},
  );
  const { data = [], loading: testimonialLoading } = useSelector(
    (state) => state.testimonial || {},
  );

  useEffect(() => {
    if (!data.length) return;
    const transformed = data
      .filter((t) => t.status === "active")
      .map((t) => ({
        id: t.id,
        name: t.name,
        text: t.description,
        photo: t.image,
        rating: parseFloat(t.stars),
        testimonial_type: t.testimonial_type,
        type_label: t.type_label || "",
      }));
    setTestimonials(transformed);
  }, [data]);

  const filteredTestimonials = testimonials.filter((testimonial) => {
    if (isHomePage) return true;
    if (menuLoading) return false;
    const mappedType = getPageTestimonialType(location.pathname, menus);
    return (
      testimonial.testimonial_type === mappedType ||
      testimonial.testimonial_type === null
    );
  });

  const isLoading = testimonialLoading || (!isHomePage && menuLoading);

  // ---- FIXED AUTO-SCROLL LOGIC ----
  const stopAnimation = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  };

  const startAnimation = () => {
    if (
      isSwipingRef.current ||
      isHoveringRef.current ||
      selectedTestimonial ||
      !marqueeRef.current ||
      filteredTestimonials.length === 0
    ) {
      return;
    }

    stopAnimation();

    const viewport = marqueeRef.current;
    const track = viewport.querySelector(".nx-tst-marquee-track");
    if (!track) return;

    if (track.scrollWidth <= viewport.clientWidth) return;

    const SCROLL_SPEED = 1.5;

    const animate = () => {
      if (
        !isSwipingRef.current &&
        !isHoveringRef.current &&
        !selectedTestimonial &&
        viewport
      ) {
        if (
          viewport.scrollLeft + viewport.clientWidth >=
          viewport.scrollWidth - 5
        ) {
          viewport.scrollLeft = 0;
        } else {
          viewport.scrollLeft += SCROLL_SPEED;
        }
      }
      if (
        !isSwipingRef.current &&
        !isHoveringRef.current &&
        !selectedTestimonial
      ) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        animationRef.current = null;
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    if (!isLoading && filteredTestimonials.length > 0 && !selectedTestimonial) {
      setTimeout(() => startAnimation(), 100);
    } else {
      stopAnimation();
    }
    return () => stopAnimation();
  }, [filteredTestimonials.length, isLoading, selectedTestimonial]);

  useEffect(() => {
    const handleResize = () => {
      if (
        !isLoading &&
        filteredTestimonials.length > 0 &&
        !selectedTestimonial
      ) {
        stopAnimation();
        startAnimation();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isLoading, filteredTestimonials.length, selectedTestimonial]);

  // ---- Mouse / Touch events ----
  const handleMouseEnter = () => {
    isHoveringRef.current = true;
    stopAnimation();
  };
  const handleMouseLeave = () => {
    isHoveringRef.current = false;
    startAnimation();
  };
  const handleTouchStart = (e) => {
    isSwipingRef.current = true;
    stopAnimation();
    if (touchTimeoutRef.current) clearTimeout(touchTimeoutRef.current);
    setIsDragging(true);
    setStartX(e.touches[0].pageX - marqueeRef.current.offsetLeft);
    setScrollLeft(marqueeRef.current.scrollLeft);
  };
  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - marqueeRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    marqueeRef.current.scrollLeft = scrollLeft - walk;
  };
  const handleTouchEnd = () => {
    setIsDragging(false);
    if (touchTimeoutRef.current) clearTimeout(touchTimeoutRef.current);
    touchTimeoutRef.current = setTimeout(() => {
      isSwipingRef.current = false;
      if (!selectedTestimonial && !isHoveringRef.current) startAnimation();
    }, 100);
  };
  const handleMouseDown = (e) => {
    setIsDragging(true);
    isSwipingRef.current = true;
    stopAnimation();
    setStartX(e.pageX - marqueeRef.current.offsetLeft);
    setScrollLeft(marqueeRef.current.scrollLeft);
    marqueeRef.current.style.cursor = "grabbing";
  };
  const handleMouseMove = (e) => {
    if (!isDragging || !marqueeRef.current) return;
    e.preventDefault();
    const x = e.pageX - marqueeRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    marqueeRef.current.scrollLeft = scrollLeft - walk;
  };
  const handleMouseUp = () => {
    setIsDragging(false);
    isSwipingRef.current = false;
    if (marqueeRef.current) marqueeRef.current.style.cursor = "grab";
    startAnimation();
  };

  const openTestimonialPopup = (testimonial) => {
    setSelectedTestimonial(testimonial);
    document.body.style.overflow = "hidden";
    stopAnimation();
  };
  const closeTestimonialPopup = () => {
    setSelectedTestimonial(null);
    document.body.style.overflow = "auto";
    isSwipingRef.current = false;
    if (!isHoveringRef.current) startAnimation();
  };

  const getInfiniteTestimonials = () => {
    if (filteredTestimonials.length === 0) return [];
    return [
      ...filteredTestimonials,
      ...filteredTestimonials,
      ...filteredTestimonials,
    ];
  };
  const infiniteTestimonials = getInfiniteTestimonials();

  if (!isLoading && !isHomePage && filteredTestimonials.length === 0)
    return null;

  return (
    <section id="nx-testimonials" className="nx-tst-wrapper">
      <div className="nx-tst-bg-glow nx-tst-glow-1"></div>
      <div className="nx-tst-bg-glow nx-tst-glow-2"></div>
      <div className="nx-tst-bg-glow nx-tst-glow-3"></div>
      <div className="nx-tst-bg-glow nx-tst-glow-4"></div>
      <div className="nx-tst-bg-glow nx-tst-glow-5"></div>

      <div className="nx-tst-container">
        <div className="nx-tst-header">
          <h2 className="nx-tst-title">{title}</h2>
          <p className="nx-tst-subtitle">{subtitle}</p>
        </div>

        {isLoading ? (
          <div className="nx-tst-status-box">
            <div className="nx-tst-spinner"></div>
            <p>Gathering stories...</p>
          </div>
        ) : filteredTestimonials.length > 0 ? (
          <div
            className="nx-tst-marquee-viewport"
            ref={marqueeRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              cursor: "grab",
              overflowX: "auto",
              scrollBehavior: "auto",
            }}
          >
            <div className="nx-tst-marquee-track">
              {infiniteTestimonials.map((testimonial, index) => (
                <div
                  key={`${testimonial.id}-${index}`}
                  className="nx-tst-card"
                  onClick={() => openTestimonialPopup(testimonial)}
                >
                  <i className="fas fa-quote-right nx-tst-card-quote"></i>

                  <div className="nx-tst-card-top">
                    <img
                      src={
                        testimonial.photo || "https://via.placeholder.com/150"
                      }
                      alt={testimonial.name}
                      className="nx-tst-avatar"
                    />
                    <div className="nx-tst-user-info">
                      <h4 className="nx-tst-user-name">{testimonial.name}</h4>
                      {testimonial.type_label && (
                        <span className="nx-tst-type-label">
                          {testimonial.type_label}
                        </span>
                      )}
                      <div className="nx-tst-stars">
                        {[1, 2, 3, 4, 5].map((star) => {
                          const rating = testimonial.rating;
                          if (star <= Math.floor(rating))
                            return <i key={star} className="fas fa-star"></i>;
                          if (
                            star === Math.floor(rating) + 1 &&
                            rating % 1 !== 0
                          )
                            return (
                              <i
                                key={star}
                                className="fas fa-star-half-alt"
                              ></i>
                            );
                          return <i key={star} className="far fa-star"></i>;
                        })}
                      </div>
                    </div>
                  </div>
                  <p className="nx-tst-card-text">{testimonial.text}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="nx-tst-status-box">
            <p>No testimonials available</p>
          </div>
        )}

        {selectedTestimonial && (
          <div className="nx-tst-modal-overlay" onClick={closeTestimonialPopup}>
            <div
              className="nx-tst-modal-box"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="nx-tst-modal-close"
                onClick={closeTestimonialPopup}
              >
                ×
              </button>
              <div className="nx-tst-modal-content">
                <div className="nx-tst-modal-image">
                  <img
                    src={
                      selectedTestimonial.photo ||
                      "https://via.placeholder.com/400"
                    }
                    alt={selectedTestimonial.name}
                  />
                </div>
                <div className="nx-tst-modal-details">
                  <h3 className="nx-tst-modal-name">
                    {selectedTestimonial.name}
                  </h3>
                  {selectedTestimonial.type_label && (
                    <span className="nx-tst-modal-type-label">
                      {selectedTestimonial.type_label}
                    </span>
                  )}
                  <div className="nx-tst-modal-stars">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const rating = selectedTestimonial.rating;
                      if (star <= Math.floor(rating))
                        return <i key={star} className="fas fa-star"></i>;
                      if (star === Math.floor(rating) + 1 && rating % 1 !== 0)
                        return (
                          <i key={star} className="fas fa-star-half-alt"></i>
                        );
                      return <i key={star} className="far fa-star"></i>;
                    })}
                  </div>
                  <p className="nx-tst-modal-text">
                    {selectedTestimonial.text}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TestimonialsSection;
